import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@/contexts/ModalContext'
import { FilterDropdown } from '@/components/Filters/FilterDropdown'

import * as S from './styles'

import type {
  FilterType,
  OrderType
} from '@/types/components/CollectionsDragDrop'

export interface CollectionFiltersModalConfig {
  defaultValues: {
    filter: FilterType
    order: OrderType
  }
  changeFilters: (filter: FilterType, order: OrderType) => void
}

export interface CollectionFiltersModalProps {
  config: CollectionFiltersModalConfig
}

export const CollectionFiltersModal = ({
  config: { defaultValues, changeFilters }
}: CollectionFiltersModalProps) => {
  const [currentFilter, setCurrentFilter] = useState<FilterType>(
    defaultValues.filter
  )
  const [currentOrder, setCurrentOrder] = useState<OrderType>(
    defaultValues.order
  )

  const { t } = useTranslation()
  const { closeModal } = useModal()

  const filterOptions = [
    { key: 'all', label: t('categories.all') },
    { key: '3d', label: t('labels.3d_contents') },
    { key: 'slide', label: t('labels.slides') },
    { key: 'radiology', label: t('modules.radiology') }
  ]

  const orderOptions = [
    { key: 'recents', label: t('labels.recentsOrder') },
    { key: 'alphabetical', label: t('labels.alphabeticalOrder') },
    { key: 'contentType', label: t('labels.contentTypeOrder') }
  ]

  const onSubmit = useCallback(() => {
    changeFilters(currentFilter, currentOrder)
    closeModal()
  }, [currentFilter, currentOrder, changeFilters, closeModal])

  return (
    <S.Container>
      <S.SelectsContainer>
        <FilterDropdown
          currentKey={currentOrder}
          onChange={key => setCurrentOrder(key as OrderType)}
          options={orderOptions}
          labelPrefix={`${t('labels.order')}: `}
          variant="inline"
        />
        <FilterDropdown
          currentKey={currentFilter}
          onChange={key => setCurrentFilter(key as FilterType)}
          options={filterOptions}
          fixedLabel={t('labels.type')}
          variant="inline"
        />
      </S.SelectsContainer>
      <S.ButtonsContainer>
        <S.Button onClick={closeModal}>{t('buttons.cancel')}</S.Button>
        <S.Button type="submit" onClick={onSubmit}>
          {t('labels.filter')}
        </S.Button>
      </S.ButtonsContainer>
    </S.Container>
  )
}
